import { useState } from 'react';
import { useAppContext } from '../../context/AppContext';

export default function OrdersTable() {
    const { orders, updateOrderStatus, deleteOrder, products, clients } = useAppContext();
    const [filter, setFilter] = useState('all');
    const [searchTerm, setSearchTerm] = useState('');
    const [dateFilter, setDateFilter] = useState('');
    const [clientFilter, setClientFilter] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10;

    const getStatusBadge = (status) => {
        const statusConfig = {
            'pendente': { bg: 'warning', icon: 'bi-clock-history', label: 'Pendente' },
            'confirmado': { bg: 'info', icon: 'bi-check-circle', label: 'Confirmado' },
            'preparando': { bg: 'primary', icon: 'bi-gear', label: 'Preparando' },
            'saiu-entrega': { bg: 'secondary', icon: 'bi-truck', label: 'Saiu p/ entrega' },
            'entregue': { bg: 'success', icon: 'bi-check-circle-fill', label: 'Entregue' },
            'cancelado': { bg: 'danger', icon: 'bi-x-circle-fill', label: 'Cancelado' },
        };

        const config = statusConfig[status] || statusConfig['pendente'];

        return (
            <span className={`badge bg-${config.bg}`}>
                <i className={`bi ${config.icon} me-1`}></i>{config.label}
            </span>
        );
    };

    const handleStatusChange = async (orderId, newStatus) => {
        try {
            await updateOrderStatus(orderId, newStatus);
        } catch (err) {
            alert('Erro ao atualizar status do pedido');
        }
    };

    const handleDelete = async (orderId, orderNumber) => {
        if (window.confirm(`Tem certeza que deseja excluir o pedido ${orderNumber}?`)) {
            try {
                await deleteOrder(orderId);
            } catch (err) {
                alert('Erro ao excluir pedido');
            }
        }
    };

    const handleCopyLink = (orderId) => {
        const link = `${window.location.origin}/pedido/${orderId}`;
        navigator.clipboard.writeText(link).then(() => {
            alert('Link copiado! Você pode compartilhar com o cliente.');
        }).catch(() => {
            alert('Não foi possível copiar o link');
        });
    };

    const formatDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toLocaleDateString('pt-BR');
    };

    // Filtros combinados
    let filteredOrders = orders;

    // Filtro por status
    if (filter !== 'all') {
        filteredOrders = filteredOrders.filter(order => order.status === filter);
    }

    // Filtro por cliente
    if (clientFilter) {
        filteredOrders = filteredOrders.filter(order => order.clientId === clientFilter);
    }

    // Filtro por data de entrega
    if (dateFilter) {
        filteredOrders = filteredOrders.filter(order => order.deliveryDate === dateFilter);
    }

    // Busca por termo (número do pedido, nome do cliente, telefone)
    if (searchTerm) {
        const searchLower = searchTerm.toLowerCase();
        filteredOrders = filteredOrders.filter(order =>
            order.orderNumber.toLowerCase().includes(searchLower) ||
            order.clientName.toLowerCase().includes(searchLower) ||
            (order.clientPhone && order.clientPhone.includes(searchTerm))
        );
    }

    // Ordenação por data de criação (mais recentes primeiro)
    const sortedOrders = [...filteredOrders].sort((a, b) =>
        new Date(b.createdAt) - new Date(a.createdAt)
    );

    // Paginação
    const totalPages = Math.ceil(sortedOrders.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const currentOrders = sortedOrders.slice(startIndex, endIndex);

    const goToPage = (page) => {
        if (page >= 1 && page <= totalPages) {
            setCurrentPage(page);
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    };

    // Reset page when filters change
    const handleFilterChange = (newFilter) => {
        setFilter(newFilter);
        setCurrentPage(1);
    };

    const handleClientFilterChange = (clientId) => {
        setClientFilter(clientId);
        setCurrentPage(1);
    };

    const handleDateFilterChange = (date) => {
        setDateFilter(date);
        setCurrentPage(1);
    };

    const handleSearchChange = (term) => {
        setSearchTerm(term);
        setCurrentPage(1);
    };

    const clearFilters = () => {
        setFilter('all');
        setSearchTerm('');
        setDateFilter('');
        setClientFilter('');
        setCurrentPage(1);
    };

    if (!orders.length) {
        return (
            <div className="alert alert-light border">
                <div className="text-center py-4">
                    <div className="fs-1 mb-2"><i className="bi bi-clipboard"></i></div>
                    <h5>Nenhum pedido cadastrado</h5>
                    <p className="text-muted mb-0">
                        Crie seu primeiro pedido usando o formulário ao lado
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className="card shadow-sm">
            <div className="card-body">
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <h2 className="h5 mb-0"><i className="bi bi-clipboard me-2"></i>Pedidos ({sortedOrders.length})</h2>

                    {(filter !== 'all' || searchTerm || dateFilter || clientFilter) && (
                        <button className="btn btn-sm btn-outline-secondary" onClick={clearFilters}>
                            <i className="bi bi-x-circle me-1"></i>Limpar filtros
                        </button>
                    )}
                </div>

                {/* Filtros */}
                <div className="row g-2 mb-3">
                    <div className="col-12 col-md-4">
                        <label className="form-label small fw-semibold">Buscar</label>
                        <input
                            type="text"
                            className="form-control form-control-sm"
                            placeholder="Nº pedido, cliente, telefone..."
                            value={searchTerm}
                            onChange={(e) => handleSearchChange(e.target.value)}
                        />
                    </div>

                    <div className="col-6 col-md-2">
                        <label className="form-label small fw-semibold">Status</label>
                        <select
                            className="form-select form-select-sm"
                            value={filter}
                            onChange={(e) => handleFilterChange(e.target.value)}
                        >
                            <option value="all">Todos</option>
                            <option value="pendente">Pendente</option>
                            <option value="confirmado">Confirmado</option>
                            <option value="preparando">Preparando</option>
                            <option value="saiu-entrega">Saiu p/ entrega</option>
                            <option value="entregue">Entregue</option>
                            <option value="cancelado">Cancelado</option>
                        </select>
                    </div>

                    <div className="col-6 col-md-3">
                        <label className="form-label small fw-semibold">Cliente</label>
                        <select
                            className="form-select form-select-sm"
                            value={clientFilter}
                            onChange={(e) => handleClientFilterChange(e.target.value)}
                        >
                            <option value="">Todos os clientes</option>
                            {clients.map((client) => (
                                <option key={client.id} value={client.id}>
                                    {client.name}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="col-6 col-md-3">
                        <label className="form-label small fw-semibold">Data de entrega</label>
                        <input
                            type="date"
                            className="form-control form-control-sm"
                            value={dateFilter}
                            onChange={(e) => handleDateFilterChange(e.target.value)}
                        />
                    </div>
                </div>

                <div className="table-responsive">
                    <table className="table table-hover align-middle">
                        <thead>
                            <tr>
                                <th>Pedido</th>
                                <th>Cliente</th>
                                <th>Entrega</th>
                                <th>Itens</th>
                                <th>Total</th>
                                <th>Status</th>
                                <th className="text-end">Ações</th>
                            </tr>
                        </thead>
                        <tbody>
                            {currentOrders.map((order) => (
                                <tr key={order.id}>
                                    <td>
                                        <div className="fw-semibold">{order.orderNumber}</div>
                                        <div className="small text-muted">
                                            {new Date(order.createdAt).toLocaleDateString('pt-BR')} às {' '}
                                            {new Date(order.createdAt).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                                        </div>
                                    </td>
                                    <td>
                                        <div className="fw-semibold">{order.clientName}</div>
                                        {order.clientPhone && (
                                            <div className="small text-muted"><i className="bi bi-telephone me-1"></i>{order.clientPhone}</div>
                                        )}
                                    </td>
                                    <td>
                                        <div className="small">
                                            <i className="bi bi-calendar me-1"></i>{formatDate(order.deliveryDate)}
                                        </div>
                                        {order.deliveryTime && (
                                            <div className="small text-muted"><i className="bi bi-clock me-1"></i>{order.deliveryTime}</div>
                                        )}
                                    </td>
                                    <td>
                                        <div className="small">
                                            {order.items.length} {order.items.length === 1 ? 'item' : 'itens'}
                                        </div>
                                    </td>
                                    <td>
                                        <div className="fw-bold">R$ {order.totalAmount.toFixed(2)}</div>
                                    </td>
                                    <td>
                                        <select
                                            className="form-select form-select-sm"
                                            value={order.status}
                                            onChange={(e) => handleStatusChange(order.id, e.target.value)}
                                            style={{ minWidth: '150px' }}
                                        >
                                            <option value="pendente">Pendente</option>
                                            <option value="confirmado">Confirmado</option>
                                            <option value="preparando">Preparando</option>
                                            <option value="saiu-entrega">Saiu p/ entrega</option>
                                            <option value="entregue">Entregue</option>
                                            <option value="cancelado">Cancelado</option>
                                        </select>
                                    </td>
                                    <td className="text-end">
                                        <button
                                            className="btn btn-sm btn-outline-success me-1"
                                            onClick={() => handleCopyLink(order.id)}
                                            title="Copiar link para compartilhar"
                                        >
                                            <i className="bi bi-link-45deg"></i>
                                        </button>
                                        <button
                                            className="btn btn-sm btn-outline-primary me-1"
                                            onClick={() => window.open(`/pedido/${order.id}`, '_blank')}
                                            title="Ver detalhes"
                                        >
                                            <i className="bi bi-eye"></i>
                                        </button>
                                        <button
                                            className="btn btn-sm btn-outline-danger"
                                            onClick={() => handleDelete(order.id, order.orderNumber)}
                                            title="Excluir"
                                        >
                                            <i className="bi bi-trash"></i>
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {/* Paginação */}
                {totalPages > 1 && (
                    <div className="mt-4 d-flex justify-content-center align-items-center">
                        <nav>
                            <ul className="pagination pagination-sm mb-0">
                                <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                                    <button className="page-link" onClick={() => goToPage(currentPage - 1)}>
                                        <i className="bi bi-chevron-left"></i>
                                    </button>
                                </li>

                                {[...Array(totalPages)].map((_, index) => {
                                    const page = index + 1;
                                    // Mostrar apenas algumas páginas ao redor da atual
                                    if (
                                        page === 1 ||
                                        page === totalPages ||
                                        (page >= currentPage - 1 && page <= currentPage + 1)
                                    ) {
                                        return (
                                            <li key={page} className={`page-item ${currentPage === page ? 'active' : ''}`}>
                                                <button className="page-link" onClick={() => goToPage(page)}>
                                                    {page}
                                                </button>
                                            </li>
                                        );
                                    } else if (page === currentPage - 2 || page === currentPage + 2) {
                                        return (
                                            <li key={page} className="page-item disabled">
                                                <span className="page-link">...</span>
                                            </li>
                                        );
                                    }
                                    return null;
                                })}

                                <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                                    <button className="page-link" onClick={() => goToPage(currentPage + 1)}>
                                        <i className="bi bi-chevron-right"></i>
                                    </button>
                                </li>
                            </ul>
                        </nav>
                        <div className="ms-3 text-muted small">
                            Página {currentPage} de {totalPages} ({sortedOrders.length} {sortedOrders.length === 1 ? 'pedido' : 'pedidos'})
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
